<template>

	<div class="next-step-page">

		<div class="next-step-heading">
			<div class="heading-title-row">
				<button type="button" class="back-button" @click="$emit('back')" aria-label="back">
					<AppChevronIcon direction="left" :size="16" />
				</button>
				<h2 class="next-step-title">{{ getString('funnel_next_title') }}</h2>
			</div>
			<p class="next-step-subtitle">{{ getString('funnel_next_subtitle') }}</p>
			<p class="next-step-intro">{{ getString('funnel_next_intro') }}</p>
		</div>

		<div class="next-step-summary">
			<div class="summary-img">
				<img :src="card.image" :alt="card.title" />
			</div>
			<div class="summary-bullets">
				<p class="summary-bullets-title">{{ getString('funnel_next_summary_title') }}</p>
				<div class="summary-bullet" v-for="(bullet, index) in card.bullets" :key="index">
					<span class="bullet-dot"></span>
					<p class="bullet-text" v-html="bullet"></p>
				</div>
			</div>
		</div>

		<div class="next-step-how">
			<p class="how-title">{{ getString('funnel_next_how_title') }}</p>
			<p class="how-subtitle">{{ getString('funnel_next_how_subtitle') }}</p>
		</div>

		<div class="next-step-options">

			<div class="option-card">
				<div class="option-top">
					<div class="option-heading">
						<span class="option-icon" v-html="getIcon('iconCalendar')"></span>
						<h4 class="option-title">{{ getString('funnel_next_opt1_title') }}</h4>
					</div>
					<p class="option-lead" v-html="getString('funnel_next_opt1_lead')"></p>
					<div class="option-bullets">
						<div class="option-bullet" v-for="key in ['funnel_next_opt1_b1', 'funnel_next_opt1_b2', 'funnel_next_opt1_b3']" :key="key">
							<span class="bullet-dot"></span>
							<p class="bullet-text">{{ getString(key) }}</p>
						</div>
					</div>
				</div>
				<div class="option-footer">
					<AppButton class="option-cta" :text="getString('funnel_next_opt1_cta')" @click="$emit('book')" />
					<p class="option-note">{{ getString('funnel_next_opt1_note') }}</p>
				</div>
			</div>

			<div class="option-card">
				<div class="option-top">
					<div class="option-heading">
						<span class="option-icon" v-html="getIcon('iconShop')"></span>
						<h4 class="option-title">{{ getString('funnel_next_opt2_title') }}</h4>
					</div>
					<p class="option-lead" v-html="getString('funnel_next_opt2_lead')"></p>
					<div class="option-bullets">
						<div class="option-bullet" v-for="key in ['funnel_next_opt2_b1', 'funnel_next_opt2_b2', 'funnel_next_opt2_b3']" :key="key">
							<span class="bullet-dot"></span>
							<p class="bullet-text">{{ getString(key) }}</p>
						</div>
					</div>
				</div>
				<div class="option-footer">
					<AppButton outline class="option-cta" :text="getString('funnel_next_opt2_cta')" @click="$emit('finish')" />
					<p class="option-note">{{ getString('funnel_next_opt2_note') }}</p>
				</div>
			</div>

		</div>

	</div>

</template>

<script>
import {useLanguageStore, useSvgStore} from "@/shared/stores";
import AppButton from "@/shared/components/AppButton.vue";
import AppChevronIcon from "@/shared/components/AppChevronIcon.vue";

export default {
	name: "FunnelNextStep",
	setup() {
		const languageStore = useLanguageStore();
		const svgStore = useSvgStore();
		return {getString: languageStore.getString, getIcon: svgStore.get};
	},
	components: {AppButton, AppChevronIcon},
	emits: ["back", "book", "finish"],
	props: {
		card: {type: Object, required: true},
	},
}
</script>

<style lang="scss" scoped>
.next-step-page {
	display: flex;
	flex-direction: column;
	gap: 30px;
	width: 100%;
	padding-right: 12px;
}

.next-step-heading {
	display: flex;
	flex-direction: column;
	gap: 6px;
	width: 100%;

	.heading-title-row {
		display: flex;
		align-items: center;
		gap: 12px;
	}

	.back-button {
		@include centered;
		flex-shrink: 0;
		width: 24px;
		height: 24px;
		background: none;
		border: none;
		padding: 0;
		cursor: pointer;
	}

	.next-step-title {
		font-size: 32px;
		font-weight: 700;
		color: var(--color-primary);
	}

	.next-step-subtitle {
		font-size: 22px;
		font-weight: 700;
		color: var(--color-txt);
	}

	.next-step-intro {
		font-size: 16px;
		color: var(--color-txt);
	}
}

.next-step-summary {
	display: flex;
	align-items: center;
	gap: 20px;
	width: 100%;

	.summary-img {
		@include centered;
		flex: 1 1 0;
		height: 163px;
		min-height: 163px;
		border-radius: 13px;
		overflow: hidden;
		background-color: rgba(var(--rgb-primary), .05);
		box-shadow: inset 2px 2px 5px rgba(var(--rgb-primary), .4);

		img {
			width: 100%;
			height: 100%;
			object-fit: cover;
		}
	}

	.summary-bullets {
		display: flex;
		flex-direction: column;
		gap: 14px;
		flex: 1 1 0;
		min-width: 0;
	}

	.summary-bullets-title {
		font-size: 22px;
		font-weight: 700;
		color: var(--color-txt);
	}
}

.next-step-how {
	display: flex;
	flex-direction: column;
	gap: 5px;
	width: 100%;
	color: var(--color-txt);

	.how-title {
		font-size: 22px;
		font-weight: 700;
	}

	.how-subtitle {
		font-size: 16px;
	}
}

.next-step-options {
	display: flex;
	align-items: stretch;
	gap: 20px;
	width: 100%;
	padding-bottom: 20px;
}

.option-card {
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	gap: 20px;
	flex: 1 1 0;
	padding: 15px;
	border-radius: 13px;
	background-color: var(--color-bg);
	box-shadow: 5px 5px 10px rgba(var(--rgb-primary), .2),
				inset -1px -1px 4px rgba(var(--rgb-primary), .5);

	.option-top {
		display: flex;
		flex-direction: column;
		gap: 20px;
		width: 100%;
	}

	.option-heading {
		display: flex;
		flex-direction: column;
		gap: 10px;
		width: 100%;

		.option-icon {
			@include centered;
			justify-content: flex-start;
			height: 36px;

			:deep(svg) {
				height: 36px;
				width: auto;
				.fill { fill: var(--color-primary); }
				.stroke { stroke: var(--color-primary); }
			}
		}
	}

	.option-title {
		font-size: 20px;
		font-weight: 700;
		color: var(--color-primary);
	}

	.option-lead {
		font-size: 18px;
		color: var(--color-txt);

		:deep(b), :deep(strong) { font-weight: 700; }
	}

	.option-bullets {
		display: flex;
		flex-direction: column;
		gap: 14px;
		width: 100%;
	}

	.option-footer {
		display: flex;
		flex-direction: column;
		gap: 10px;
		width: 100%;
	}

	.option-cta {
		width: 100%;
		max-width: 100%;
		:deep(.base-button) {
			width: 100%;
			max-width: unset;
		}
	}

	.option-note {
		font-size: 14px;
		color: var(--color-txt);
	}
}

.bullet-dot {
	flex-shrink: 0;
	width: 7px;
	height: 7px;
	margin-top: 7px;
	border-radius: 50%;
	background-color: var(--color-primary);
}

.summary-bullet,
.option-bullet {
	display: flex;
	align-items: flex-start;
	gap: 10px;

	.bullet-text {
		font-size: 16px;
		line-height: 20px;
		color: var(--color-txt);

		:deep(b), :deep(strong) { font-weight: 700; }
	}
}

/* Mobile / tablet: stack the summary and the option cards */
@media (max-width: 768px) {
	.next-step-page {
		gap: 20px;
	}

	.next-step-heading {
		.next-step-title { font-size: 26px; }
		.next-step-subtitle { font-size: 18px; }
	}

	.next-step-summary {
		flex-direction: column;
		align-items: stretch;
	}

	.next-step-options {
		flex-direction: column;
		align-items: center;

		.option-card {
			width: 100%;
			max-width: 100%;
		}
	}
}
</style>
